import { useNavigate } from 'react-router-dom'
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb'
import { SubscribeCta } from '@/components/website/SubscribeCta'
import { useWebsiteGallery } from '@/services/httpServices/queries/useWebsite'

export type GalleryTab = 'image' | 'video'

const TABS: { id: GalleryTab; label: string; to: string }[] = [
  { id: 'image', label: 'Image Gallery', to: '/image-gallery' },
  { id: 'video', label: 'Video Gallery', to: '/video-gallery' },
]

/**
 * Single gallery page with an in-page tab menu. The tab is driven by the route
 * (`/image-gallery` or `/video-gallery`) so links, refresh and back/forward all
 * stay in sync. Assets are managed in the dashboard and fetched from the API,
 * falling back to bundled demo content when offline.
 */
export function Gallery({ initialTab }: { initialTab: GalleryTab }) {
  const navigate = useNavigate()
  const active = initialTab
  const title = active === 'video' ? 'Video Gallery' : 'Image Gallery'
  const { images, videos } = useWebsiteGallery()

  return (
    <>
      <PageBreadcrumb title={title} />

      <section className="gallery-section pt-100 md-pt-80 pb-100 md-pb-80">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="pricing-tabs wow fadeInUp" data-wow-delay=".2s">
                <nav>
                  <div className="nav nav-tabs" role="tablist">
                    {TABS.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={active === tab.id}
                        className={`nav-link${active === tab.id ? ' active' : ''}`}
                        onClick={() => navigate(tab.to)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </nav>
              </div>
            </div>
          </div>

          {active === 'image' ? (
            <div className="row">
              {images.map((image, index) => (
                <div className="col-lg-4 col-md-6 col-sm-6" key={image.id}>
                  <div className="photo-gallery">
                    <div className="photo-gallery-image">
                      <figure className="image-anime">
                        <img src={image.image} alt={image.title || `photo gallery ${index + 1}`} />
                      </figure>
                    </div>
                    <div className="photo-gallery-icon">
                      <a
                        className="photo-popup"
                        href={image.image}
                        aria-label={image.title || 'photo gallery'}
                      >
                        <i className="fa-solid fa-plus"></i>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="row">
              {videos.map((video, index) => (
                <div className="col-lg-4 col-md-6 col-sm-6" key={video.id}>
                  <div className="video-gallery-item">
                    <a
                      href={video.videoUrl}
                      className="video-popup"
                      target="_blank"
                      rel="noreferrer"
                      aria-label={video.title || `video gallery ${index + 1}`}
                    >
                      <figure>
                        <img
                          src={video.image}
                          alt={video.title || `video gallery ${index + 1}`}
                        />
                      </figure>
                      <div className="play-button">
                        <i className="fas fa-play"></i>
                      </div>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <SubscribeCta />
    </>
  )
}
